<template>
  <div>
    <div class="d-flex justify-content-between">
      <h1 class="page-header">{{ $t('purchaseRequest.confirmation') }}</h1>
      <ts-breadcrumb
        :items="[
          { text: $t('home'), href: '/' },
          {
            text: $t('purchaseRequest.pageTitle'),
            href: '/admin/purchase-requests'
          },
          {
            text: $t('confirm'),
            active: true
          }
        ]"
      />
    </div>
    <ts-card>
      <ts-card-wrapper>
        <ts-loading-banner :loading="loading">
          <form class="form-horizontal ts-space-y-3">
            <div class="row">
              <div class="col-md-12">
                <Info
                  v-model="model"
                  :validate="errors"
                  class="tw-p-3 tw-rounded"
                />
              </div>
            </div>
            <div class="row">
              <div class="col-md-12">
                <ItemList
                  v-model="model"
                  :validate="errors"
                  class="ts-border ts-rounded"
                />
              </div>
            </div>
            <div class="row ts-mt-4">
              <div
                class="col-md-12 ts-flex ts-items-center ts-space-x-3 ts-justify-end"
              >
                <ts-button
                  class="ts-w-20"
                  @click.prevent="
                    () => $router.push({ name: 'purchase-request' })
                  "
                >
                  {{ $t('cancel') }}
                </ts-button>
                <ts-button
                  color="danger"
                  class="ts-w-20"
                  @click.prevent="onShowForm(3)"
                >
                  {{ $t('reject') }}
                </ts-button>
                <ts-button
                  outline
                  color="danger"
                  @click.prevent="onShowForm(4)"
                >
                  {{ $t('purchaseRequest.reverse') }}
                </ts-button>
                <ts-button
                  color="success"
                  class="ts-w-20"
                  @click.prevent="onShowForm(2)"
                >
                  {{ $t('approve') }}
                </ts-button>
              </div>
            </div>
          </form>
        </ts-loading-banner>
      </ts-card-wrapper>
    </ts-card>
    <!-- modal show pop-up confirm purchase request -->
    <a-modal
      v-model="show_form"
      :title="$t('purchaseRequest.confirmTitle')"
      :footer="null"
      :zIndex="1020"
      :maskClosable="false"
      width="450px"
    >
      <div class="ts-space-y-3">
        <div class="row" v-if="action.display">
          <div class="col-md-12">
            <label class="required">
              {{ $t('purchaseRequest.vendor') }}
            </label>
            <a-select
              v-model="model.vendor_id"
              :options="vendors"
              class="ts-w-full"
              :class="{
                'is-invalid': errors.has('vendor_id')
              }"
              show-search
              option-filter-prop="children"
              @change="onChangeVendor"
            ></a-select>
            <div class="validate" v-if="errors.has('vendor_id')">
              {{ errors.first('vendor_id') }}
            </div>
          </div>
        </div>
        <div class="row" v-if="action.display">
          <div class="col-md-12">
            <label>
              {{ $t('purchaseRequest.purchaseTypes') }}
            </label>
            <a-select
              v-model="model.purchase_type_id"
              :options="purchaseTypes"
              class="ts-w-full"
              :class="{
                'is-invalid': errors.has('purchase_type_id')
              }"
              show-search
              option-filter-prop="children"
            ></a-select>
            <div class="validate" v-if="errors.has('purchase_type_id')">
              {{ errors.first('purchase_type_id') }}
            </div>
          </div>
        </div>
        <div class="row">
          <div class="col-md-12">
            <label
              :class="[3, 4].includes(model.approve_status) ? 'required' : ''"
            >
              {{ $t('purchaseRequest.comments') }}
            </label>
            <textarea
              v-model="model.comments"
              rows="3"
              class="form-control ts-w-full"
            ></textarea>
            <div class="validate" v-if="errors.has('comments')">
              {{ errors.first('comments') }}
            </div>
          </div>
        </div>
        <div class="row ts-mt-3">
          <div class="col-md-12 ts-flex ts-justify-end ts-space-x-3">
            <ts-button
              class="ts-w-20"
              :waiting="loading"
              @click.prevent="() => (show_form = false)"
            >
              {{ $t('cancel') }}
            </ts-button>
            <ts-button
              :color="action.color"
              :outline="model.create_po"
              class="ts-min-w-20"
              :waiting="loading"
              @click.prevent="onConfirm()"
            >
              {{ action.label }}
            </ts-button>
          </div>
        </div>
        <Spin size="large" fix v-if="loading"></Spin>
      </div>
    </a-modal>
  </div>
</template>

<script>
import { mapActions, mapState } from 'vuex'
import { Errors } from 'form-backend-validation'
import Info from './components/confirm/info.vue'
import ItemList from './components/confirm/item-list'

export default {
  name: 'purchaseRequestV2Edit',
  components: {
    Info,
    ItemList
  },
  data() {
    return {
      loading: false,
      waiting: false,
      show_form: false,
      src: '',
      errors: new Errors(),
      model: {
        pr_main_id: null,
        approve_status: null,
        vendor_id: null,
        purchase_type_id: null,
        comments: '',
        credit_day: null,
        create_po: false
      }
    }
  },
  computed: {
    ...mapState('purchaseRequest/purchaseRequest', [
      'formViewDatas',
      'edit_data'
    ]),
    vendors() {
      if (this.formViewDatas['vendors']) {
        return this.formViewDatas['vendors'].map(el => ({
          value: el.vendor_id,
          label: el.vendor_name,
          credit_day: el.credit_day
        }))
      }
      return []
    },
    purchaseTypes() {
      if (this.formViewDatas['purchaseTypes']) {
        return this.formViewDatas['purchaseTypes'].map(el => ({
          value: el.purchase_type_id,
          label: el.purchase_type
        }))
      }
      return []
    },
    action() {
      switch (this.model.approve_status) {
        case 2:
          return {
            label: this.model.create_po
              ? this.$t('purchaseRequest.approve&createPo')
              : this.$t('approve'),
            color: 'success',
            display: this.model.create_po
          }
        case 3:
          return {
            label: this.$t('reject'),
            color: 'danger',
            display: false
          }
        case 4:
          return {
            label: this.$t('purchaseRequest.reverse'),
            color: 'danger',
            display: false
          }
        default:
          return {
            label: '',
            color: '',
            display: false
          }
      }
    }
  },
  methods: {
    ...mapActions('purchaseRequest/purchaseRequest', [
      'getFormViewModel',
      'find',
      'update'
    ]),
    async getPreData({ params, index }) {
      this.loading = true
      this.model.pr_main_id = this.$route.params.id
      this.find(this.$route.params.id)
        .then(() => {})
        .finally(() => (this.loading = false))

      this.getFormViewModel({
        index: index,
        params: {
          fnName: 'vendors,purchaseTypes'
        }
      })
    },
    onChangeVendor(value, option) {
      this.model.credit_day = option.data.props.credit_day
    },
    onShowForm(approve_status, create_po = false) {
      this.model.approve_status = approve_status
      this.model.create_po = create_po
      this.show_form = true
    },
    onConfirm() {
      this.loading = true
      this.errors = new Errors()
      this.$store
        .dispatch('purchaseRequest/purchaseRequest/confirm', {
          id: this.model.pr_main_id,
          data: this.model
        })
        .then(response => {
          this.$toasted.success(response.message)
          this.$router.push({ name: 'purchase-request' })
        })
        .catch(error => {
          this.$toasted.error(error.message)
          this.errors = new Errors(error.errors)
        })
        .finally(() => {
          this.loading = false
        })
    }
  },
  beforeRouteEnter(to, from, next) {
    next(vm => {
      vm.getPreData({
        params: {},
        index: undefined
      })
    })
  },
  beforeRouteLeave(to, from, next) {
    this.$store.commit('purchaseRequest/purchaseRequest/SET_EDIT_DATA', {})
    next()
  }
}
</script>
